/**
 * useArtifiDesigner -- load the Artifi headless Designer bundle and drive its
 * lifecycle (Initialize / AddToCart) from a Vue page.
 *
 * PROTOTYPE (GTM3 / Artifi Phase B). This is a risk-retirement spike, not a
 * production component. It is the FIRST code in the repo that actually loads
 * ``Artifi.headless.js`` and calls ``Artifi.Initialize`` in a browser. The
 * Phase B contract was only ever sketched in
 * ``docs/integrations/artifi/iframe-integration-sketch.md`` -- this exercises
 * it for real.
 *
 * Contract source (2026-07-01): Amla's official integration doc,
 * ``docs/integrations/artifi/steps-to-integrate-artifi-components-in-ecommerce.md``.
 * It corrected the 2026-06-29 spike, which guessed ``<div id="artifi">`` and
 * never painted a canvas.
 *
 * Key facts:
 *   - It is NOT a postMessage iframe. The bundle runs in OUR page's JS context
 *     and injects its UI into the MANDATORY ``<div id="artifi-editor">``
 *     container (the "Editor" component). This container is required for the
 *     canvas to render; we pre-create it inside our mount element.
 *   - Lifecycle + handoff are WINDOW events: ``artifi-initialized`` /
 *     ``artifi-launching-error`` on launch; ``artifi-add-to-cart-success`` /
 *     ``artifi-add-to-cart-error`` after ``AddToCart()``.
 *   - Init params (doc section 3): ``webApiClientKey, websiteId, userId,
 *     isGuest, sku``.
 *   - Artifi's allowlist matches the Origin/Referer HOST. The page must be
 *     served from an allowlisted host (e.g. ``tf-sandbox.coules.io``) or the
 *     bundle's API calls are rejected. localhost will NOT work.
 */
import { ref, shallowRef } from 'vue'
import { describeError } from "@/utils/safeErrorLog";

// Website 445 pairs with the PRODUCTION script host. The 1Password item
// "Artifi Headless -- Production" lists ``component.artifi.net``; the
// non-prod host ``integrationcomponent.artifi.net`` pairs with the sparse
// Website 673 and is NOT what our credential is bound to. Kept as a named
// constant so it is trivial to flip if Amla tells us otherwise.
export const ARTIFI_SCRIPT_URL =
    'https://component.artifi.net/artifi-headless-releases/3.0/Artifi.headless.js'

// Tenant we have credentials for. The Web API Client Key is a PUBLIC
// browser-exposed value by design (Artifi's allowlist is the real boundary),
// so embedding it here for a staging-only prototype is acceptable. The
// production component will read tenant config from the backend rather than
// hard-coding.
export const ARTIFI_WEBSITE_ID = 445
export const ARTIFI_WEB_API_CLIENT_KEY = 'c30daaf6-234a-4b64-9767-7ec9eb929ad3'

// Artifi-side user id. MUST match the id the backend bridge uploads logos
// under (ARTIFI_USER_ID in api/conf.py, "1" for Website 445 -- the tenant
// accepts only "1"). Artifi scopes uploaded photos per userId, so the
// Designer must launch as the same id to see an uploaded logo.
export const ARTIFI_USER_ID = '1'

// Idempotent module-level guard so the <script> is injected at most once even
// if the composable is used by multiple components / re-mounted.
let scriptPromise = null

/**
 * Inject the Artifi headless bundle exactly once. Resolves when
 * ``window.Artifi`` is available; rejects on script load error.
 */
function loadArtifiScript() {
    if (scriptPromise) return scriptPromise
    if (typeof window !== 'undefined' && window.Artifi) {
        scriptPromise = Promise.resolve(window.Artifi)
        return scriptPromise
    }
    scriptPromise = new Promise((resolve, reject) => {
        const el = document.createElement('script')
        el.src = ARTIFI_SCRIPT_URL
        el.async = true
        el.addEventListener('load', () => {
            if (window.Artifi) {
                resolve(window.Artifi)
            } else {
                reject(
                    new Error(
                        'Artifi.headless.js loaded but window.Artifi is undefined',
                    ),
                )
            }
        })
        el.addEventListener('error', () => {
            // Reset so a later retry can re-attempt the injection.
            scriptPromise = null
            reject(new Error(`Failed to load Artifi bundle from ${ARTIFI_SCRIPT_URL}`))
        })
        document.head.appendChild(el)
    })
    return scriptPromise
}

/**
 * Drive the Artifi Designer lifecycle.
 *
 * Returns reactive state + ``launch`` / ``addToCart`` actions. The page wires
 * its own buttons to these -- Artifi's headless model expects the HOST to own
 * the Add-to-Cart button and call ``Artifi.AddToCart()``.
 */
export function useArtifiDesigner() {
    // 'idle' | 'loading-script' | 'initializing' | 'ready' | 'error'
    const status = ref('idle')
    const errorMessage = ref(null)
    // The most recent design-handoff payload from the add-to-cart event.
    const lastPayload = shallowRef(null)
    // Captures the raw Artifi global for ad-hoc poking from the prototype.
    const artifi = shallowRef(null)

    // ---- Contract per Amla's official integration doc (2026-07-01) ----
    // docs/integrations/artifi/steps-to-integrate-artifi-components-in-ecommerce.md
    //
    // The 2026-06-29 spike guessed ``<div id="artifi">`` from bundle
    // introspection and the canvas never painted (3 empty divs). The doc
    // Amit referenced names the REAL contract:
    //
    //   * The mandatory canvas container is ``<div id="artifi-editor">`` (the
    //     "Editor" component). It is REQUIRED for the canvas to render; the
    //     guessed ``#artifi`` id is not a real Artifi container. Other widget
    //     containers (product-views, text widgets, upload, add-to-cart, ...)
    //     all require the Editor container to be present.
    //   * Initialize with ``Artifi.initialize({ webApiClientKey, websiteId,
    //     userId, isGuest, sku })`` -- the documented required param set. The
    //     bundle exposes lowercase methods (initialize / addToCart), which the
    //     doc's capitalized spelling (Initialize / AddToCart) refers to.
    //   * Success is signalled by the ``artifi-initialized`` window event, NOT
    //     just the synchronous return. Launch failures fire
    //     ``artifi-launching-error``. We resolve ``launch`` on
    //     ``artifi-initialized`` (with a timeout fallback) so status only
    //     reads 'ready' once the canvas is actually up.
    //   * Add-to-cart: ``Artifi.AddToCart()`` triggers the flow; the design
    //     handoff arrives on the ``artifi-add-to-cart-success`` window event
    //     (payload in ev.detail). ``artifi-add-to-cart-error`` on failure.
    const ARTIFI_CONTAINER_ID = 'artifi-editor'
    const EVT_INITIALIZED = 'artifi-initialized'
    const EVT_LAUNCH_ERROR = 'artifi-launching-error'
    const EVT_ADD_TO_CART_SUCCESS = 'artifi-add-to-cart-success'
    const EVT_ADD_TO_CART_ERROR = 'artifi-add-to-cart-error'
    // How long to wait for ``artifi-initialized`` before assuming the canvas
    // is up anyway (the doc does not guarantee the event fires on every
    // tenant/config; the fallback keeps the UI from hanging on 'initializing').
    const INIT_EVENT_TIMEOUT_MS = 15000

    // All window listeners this composable registers, tracked for dispose().
    const listeners = {}

    /**
     * Load the bundle and call ``window.Artifi.initialize`` to mount the
     * Designer. The bundle renders into ``<div id="artifi">``; we ensure that
     * element exists inside ``mountEl`` so the Designer lands in our layout
     * rather than being appended to <body>.
     *
     * @param {object} opts
     * @param {HTMLElement} opts.mountEl host element to render the Designer in
     * @param {string} opts.productCode  master SKU (e.g. "18000")
     * @param {string} [opts.sku]        variant SKU (encodes color, e.g. "256262")
     * @param {string} opts.userId       host-minted per-shopper id
     * @param {string} [opts.viewCode]   decoratable view (e.g. "Full Center")
     * @param {string} [opts.artifiUniqueName] Artifi UniqueName of an already-
     *   uploaded logo (from the backend bridge) to preload onto the canvas.
     *   Best-effort: the vendor-confirmed preload mechanism is still open, so
     *   this is passed to initialize + attempted post-init, and logged.
     * @param {number} [opts.width]
     * @param {number} [opts.height]
     */
    async function launch(opts) {
        const {
            mountEl,
            productCode,
            sku = null,
            userId,
            viewCode = '',
            artifiUniqueName = null,
            width = 900,
            height = 650,
        } = opts || {}
        if (!mountEl) throw new Error('useArtifiDesigner.launch: mountEl is required')
        if (!productCode) {
            throw new Error('useArtifiDesigner.launch: productCode is required')
        }
        if (!userId) throw new Error('useArtifiDesigner.launch: userId is required')

        errorMessage.value = null
        status.value = 'loading-script'
        try {
            // Ensure the bundle is injected (idempotent). After it resolves,
            // always read the LIVE ``window.Artifi``.
            await loadArtifiScript()
            const A = (typeof window !== 'undefined' && window.Artifi) || null
            if (!A || typeof A.initialize !== 'function') {
                throw new Error('window.Artifi.initialize is not available')
            }
            artifi.value = A
            status.value = 'initializing'

            // Pre-create the mandatory Editor container (<div id="artifi-editor">)
            // inside our mount so the Designer renders into our layout. Per the
            // Amla doc this container is REQUIRED for the canvas to paint.
            let host = document.getElementById(ARTIFI_CONTAINER_ID)
            if (host && host.parentElement !== mountEl) {
                host.remove()
                host = null
            }
            if (!host) {
                host = document.createElement('div')
                host.id = ARTIFI_CONTAINER_ID
                host.style.width = '100%'
                host.style.height = `${height}px`
                mountEl.innerHTML = ''
                mountEl.appendChild(host)
            }

            // (Re)register the lifecycle + handoff window listeners.
            _registerListeners()

            // Documented required init params (Amla doc section 3). The bundle
            // reads sku/userId/isGuest/websiteId/webApiClientKey; productCode +
            // viewCode are passed as best-effort extras the 3.0 bundle also
            // consumed in the spike (harmless if ignored).
            const config = {
                webApiClientKey: ARTIFI_WEB_API_CLIENT_KEY,
                websiteId: ARTIFI_WEBSITE_ID,
                userId,
                isGuest: false,
            }
            if (sku) config.sku = sku
            if (productCode) config.productCode = productCode
            if (viewCode) config.viewCode = viewCode
            // Best-effort logo preload. The vendor-confirmed mechanism to
            // auto-place an already-uploaded UniqueName onto the interactive
            // canvas is still open (see the api-reference "Phase B blockers").
            // We pass it on initialize under a plausible field; if the bundle
            // ignores it we retry via its method surface post-init below.
            if (artifiUniqueName) {
                config.designImageUniqueName = artifiUniqueName
            }

            // Resolve 'ready' on the artifi-initialized event (with a timeout
            // fallback) rather than on the synchronous return -- initialize()
            // is async and the canvas is not up until the event fires.
            await _awaitInitialized(() => {
                // eslint-disable-next-line no-console
                console.log('[ArtifiDesigner] initialize:', { ...config, webApiClientKey: '[redacted]' })
                A.initialize(config)
            })

            // Best-effort: after init, try to place the uploaded logo via the
            // bundle's method surface. The real method name is unconfirmed --
            // log the available methods so we can identify it live and ask
            // Amla for the canonical preload API. Does NOT fail the launch.
            if (artifiUniqueName) {
                _tryPreloadImage(A, artifiUniqueName)
            }

            status.value = 'ready'
        } catch (err) {
            status.value = 'error'
            errorMessage.value = err?.message || String(err)
            // eslint-disable-next-line no-console
            console.error('[ArtifiDesigner] launch failed:', describeError(err))
            throw err
        }
    }

    /**
     * Register the window event listeners the Artifi bundle dispatches:
     * ``artifi-initialized`` / ``artifi-launching-error`` (lifecycle) and
     * ``artifi-add-to-cart-success`` / ``artifi-add-to-cart-error`` (handoff).
     * Idempotent: removes any previously-registered listeners first.
     */
    function _registerListeners() {
        _removeListeners()
        listeners[EVT_ADD_TO_CART_SUCCESS] = (ev) => {
            const payload = ev?.detail ?? { event: EVT_ADD_TO_CART_SUCCESS }
            // eslint-disable-next-line no-console
            console.log('[ArtifiDesigner] add-to-cart success:', payload)
            lastPayload.value = payload
        }
        listeners[EVT_ADD_TO_CART_ERROR] = (ev) => {
            // eslint-disable-next-line no-console
            console.error('[ArtifiDesigner] add-to-cart error:', ev?.detail)
        }
        window.addEventListener(
            EVT_ADD_TO_CART_SUCCESS,
            listeners[EVT_ADD_TO_CART_SUCCESS],
        )
        window.addEventListener(
            EVT_ADD_TO_CART_ERROR,
            listeners[EVT_ADD_TO_CART_ERROR],
        )
    }

    function _removeListeners() {
        for (const [type, cb] of Object.entries(listeners)) {
            if (cb) window.removeEventListener(type, cb)
            delete listeners[type]
        }
    }

    /**
     * Invoke ``doInit`` (which calls Artifi.initialize) and resolve once the
     * bundle dispatches ``artifi-initialized``. Rejects on
     * ``artifi-launching-error``. Falls back to resolving after
     * ``INIT_EVENT_TIMEOUT_MS`` so a tenant/config that never fires the event
     * doesn't hang the UI in 'initializing'.
     */
    function _awaitInitialized(doInit) {
        return new Promise((resolve, reject) => {
            let settled = false
            const cleanup = () => {
                window.removeEventListener(EVT_INITIALIZED, onOk)
                window.removeEventListener(EVT_LAUNCH_ERROR, onErr)
                if (timer) clearTimeout(timer)
            }
            const onOk = (ev) => {
                if (settled) return
                settled = true
                cleanup()
                // eslint-disable-next-line no-console
                console.log('[ArtifiDesigner] artifi-initialized:', ev?.detail)
                resolve('event')
            }
            const onErr = (ev) => {
                if (settled) return
                settled = true
                cleanup()
                const msg =
                    ev?.detail?.message ||
                    ev?.detail ||
                    'Artifi reported a launching error'
                reject(new Error(String(msg)))
            }
            const timer = setTimeout(() => {
                if (settled) return
                settled = true
                cleanup()
                // eslint-disable-next-line no-console
                console.warn(
                    '[ArtifiDesigner] no artifi-initialized within ' +
                        `${INIT_EVENT_TIMEOUT_MS}ms; assuming ready`,
                )
                resolve('timeout')
            }, INIT_EVENT_TIMEOUT_MS)
            window.addEventListener(EVT_INITIALIZED, onOk)
            window.addEventListener(EVT_LAUNCH_ERROR, onErr)
            // doInit() calls Artifi.initialize, which can throw synchronously
            // (bad config, bundle-internal error). Without this guard a
            // synchronous throw would reject the promise but leave the two
            // window listeners + the timer leaked for INIT_EVENT_TIMEOUT_MS
            // (they aren't dispose-tracked). Run cleanup + reject deterministically.
            try {
                doInit()
            } catch (err) {
                if (!settled) {
                    settled = true
                    cleanup()
                    reject(err)
                }
            }
        })
    }

    /**
     * Best-effort: place an already-uploaded logo (Artifi UniqueName) onto
     * the canvas after init.
     *
     * The canonical preload API is NOT in the docs we have (see
     * docs/integrations/artifi/api-reference.md, "Phase B blockers"). This
     * helper logs the bundle's full method surface so the real method can be
     * identified live + confirmed with Amla, then tries the most plausible
     * candidate names if the bundle exposes them. It never throws -- a miss
     * just leaves the canvas on the bare garment (today's behaviour).
     */
    function _tryPreloadImage(A, uniqueName) {
        // Surface the available methods so we can spot the real preload API.
        const methods = []
        for (const k in A) {
            try {
                if (typeof A[k] === 'function') methods.push(k)
            } catch {
                /* some globals throw on access; skip */
            }
        }
        // eslint-disable-next-line no-console
        console.log(
            '[ArtifiDesigner] preload: Artifi methods available =',
            methods.sort(),
            '| logo UniqueName =',
            uniqueName,
        )
        // Candidate method names (unconfirmed). Call the first that exists.
        const candidates = [
            'addUserImage',
            'addImage',
            'applyUserImage',
            'addUserPhoto',
            'loadUserImage',
        ]
        for (const name of candidates) {
            if (typeof A[name] === 'function') {
                try {
                    // eslint-disable-next-line no-console
                    console.log(`[ArtifiDesigner] preload: trying A.${name}()`)
                    A[name]({ uniqueName, isExternalImg: true })
                    return
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `[ArtifiDesigner] preload: A.${name}() threw`,
                        describeError(err),
                    )
                }
            }
        }
        // eslint-disable-next-line no-console
        console.warn(
            '[ArtifiDesigner] preload: no known add-image method on the ' +
                'bundle. The logo stays available in Artifi (My Art) but is ' +
                'not auto-placed. Awaiting the vendor preload API.',
        )
    }

    /**
     * Wire the host page's "Finish / Add to Cart" button to this. Calls
     * ``Artifi.AddToCart()`` (the bundle exposes it lowercase as
     * ``addToCart``); the design handoff arrives on the
     * ``artifi-add-to-cart-success`` window event that ``launch`` listens for.
     */
    function addToCart() {
        const A = artifi.value
        // The doc names AddToCart(); the 3.0 bundle exposes it lowercase.
        // Prefer whichever the loaded bundle actually provides.
        const fn =
            (A && typeof A.addToCart === 'function' && A.addToCart) ||
            (A && typeof A.AddToCart === 'function' && A.AddToCart) ||
            null
        if (!fn) {
            throw new Error('Artifi.AddToCart is not available (launch first)')
        }
        // eslint-disable-next-line no-console
        console.log('[ArtifiDesigner] AddToCart()')
        fn.call(A)
    }

    /**
     * Tear down all window listeners registered by ``launch``. The host page
     * MUST call this from its ``onUnmounted`` -- otherwise each
     * mount->unmount leaves dead listeners bound to ``window`` whose closures
     * pin the previous instance's state.
     */
    function dispose() {
        _removeListeners()
    }

    return {
        status,
        errorMessage,
        lastPayload,
        artifi,
        launch,
        addToCart,
        dispose,
    }
}
